import { HttpClient } from "@angular/common/http";
import { computed, Injectable, signal } from "@angular/core";
import { environment } from "@environments/environment";
import { IUserInfo, IUserProfile, UserInfo } from "@scl/core/models/user-info";
import { AppResponseBase } from "@scl/shared/models/app-response-base";
import { BehaviorSubject, Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { EnableTwoFactor } from "../models/account";
import { ChangePassword } from "../models/change-password";
import { UserRole } from "../models/role";

@Injectable({
  providedIn: "root",
})
export class AccountService {
  private apiUrl = `${environment.apiUrlBase}/api/account`; 

  private readonly _currentUser: BehaviorSubject<IUserInfo | null> =  new BehaviorSubject<IUserInfo | null>(null);
  readonly currentUser$ = this._currentUser.asObservable();

  private readonly _currentProfile: BehaviorSubject<IUserProfile | null> =  new BehaviorSubject<IUserProfile | null>(null);
  readonly currentProfile$ = this._currentProfile.asObservable();

  readonly isAdmin$ = this.currentUser$.pipe(
    map((usr) => usr && usr!.roles!.find(x => x.name == UserRole.Admin) != null),
  );

  readonly isSecondaryAdmin$ = this.currentUser$.pipe(
    map((usr) => usr && usr!.roles!.find(x => x.name == UserRole.SecondaryAdmin) != null),
  );

  readonly isLO$ = this.currentUser$.pipe(
    map((usr) => usr && usr!.roles!.find(x => x.name == UserRole.LoanOfficer) != null),
  );

  readonly isProcessor$ = this.currentUser$.pipe(
    map((usr) => usr && usr!.roles!.find(x => x.name == UserRole.Processor) != null),
  );

  readonly isBranchManager$ = this.currentUser$.pipe(
    map((usr) => usr && usr!.roles!.find(x => x.name == UserRole.BranchManager) != null),
  );

  readonly showManageLoans$ = this.currentUser$.pipe(
    map(
      (usr) =>
        usr &&
        usr!.roles!.some((r) =>
          [UserRole.Admin + "", UserRole.SecondaryAdmin].includes(r.name!),
        ),
    ),
  );

  readonly showMyLoans$ = this.currentUser$.pipe(
    map(
      (usr) =>
        usr &&
        usr!.roles!.find(x => x.name == UserRole.LoanOfficer) != null &&
        usr!.roles!.every(
          (r) => ![UserRole.Admin + "", UserRole.SecondaryAdmin].includes(r.name!),
        ),
    ),
  );

  readonly showMyProcessingPayrolls$ = this.currentUser$.pipe(
    map(
      (usr) =>
        usr &&
        usr!.roles!.find(x => x.name == UserRole.Processor) != null &&
        usr!.roles!.every(
          (r) => ![UserRole.Admin + "", UserRole.SecondaryAdmin].includes(r.name!),
        ),
    ),
  );
  
  set currentUser(userInfo: IUserInfo | null) {
    this._currentUser.next(userInfo);
  }

  get currentUser() {
    return this._currentUser.getValue();
  }

   set currentProfile(userInfo: IUserProfile | null) {
    this._currentProfile.next(userInfo);
  }

  get currentProfile() {
    return this._currentProfile.getValue();
  }

  constructor(private http: HttpClient) { }

  getUserInfo(): Observable<IUserInfo> {
    const url = `${this.apiUrl}/userinfo`;
    return this.http.get<AppResponseBase<IUserInfo>>(url).pipe(
      switchMap((rs) => {
        this.currentUser = rs.result;
        return of(rs.result);
      }),
    );
  }

  getUserProfile(): Observable<IUserProfile> {
    const url = `${this.apiUrl}/userprofile`;
    return this.http.get<AppResponseBase<IUserProfile>>(url).pipe(
      switchMap((rs) => {
        this.currentProfile = rs.result;
        return of(rs.result);
      }),
    );
  }

  changePassword(
    changePassword: ChangePassword,
  ): Observable<AppResponseBase<any>> {
    const url = `${this.apiUrl}/changepassword`;
    return this.http.post<any>(url, changePassword);
  }

  resetPasswordAndProvision(
    password: string,
    passwordConfirm: string,
  ): Observable<AppResponseBase<any>> {
    const url = `${this.apiUrl}/reset-password-provision`;
    return this.http.post<AppResponseBase<any>>(url, { password, passwordConfirm });
  }

  getTwoFactor() {
    const url = `${this.apiUrl}/two-factor`;
    return this.http.get<AppResponseBase<EnableTwoFactor>>(url);
  }

  updateTwoFactor(twoFactor: EnableTwoFactor) {
    const url = `${this.apiUrl}/two-factor`;
    return this.http.put<AppResponseBase<EnableTwoFactor>>(url, twoFactor);
  }
  // Disclaimer methods
  getDisclaimers(): Observable<string> {
    const url = `${this.apiUrl}/email-signature`;
    return this.http
      .get<AppResponseBase<string>>(url)
      .pipe(map((response) => response.result || ""));
  }

  updateDisclaimers(content: string): Observable<AppResponseBase<any>> {
    const url = `${this.apiUrl}/email-signature`;
    return this.http.put<AppResponseBase<any>>(url, { content });
  }
  updateInfo(user: IUserInfo): Observable<AppResponseBase<any>> {
    let url = `${this.apiUrl}`;
    return this.http.put<AppResponseBase<any>>(url, user);
  }

  // ================================
  // Permission Helper Methods  
  // ================================

  /**
   * Checks if current user has a specific permission
   * @param permissionString - Permission string in format "Entity.Action"
   * @returns True if user has the permission or FullControl on the entity
   */
  hasPermission(permissionString: string): boolean {
    const currentPermissions = this.currentUser?.permissions ?? [];

    // Check if user has the exact permission
    if (currentPermissions.includes(permissionString)) {
      return true;
    }

    // Check if user has FullControl permission on the entity
    const entityName = permissionString.split('.')[0];
    const fullControlPermission = `${entityName}.FullControl`;
    return currentPermissions.includes(fullControlPermission);
  }

  /**
   * Checks if current user has any of the specified permissions
   * @param permissionStrings - Array of permission strings
   * @returns True if user has at least one permission
   */
  hasAnyPermission(permissionStrings: string[]): boolean {
    return permissionStrings.some(permission => this.hasPermission(permission));
  }

  /**
   * Checks if current user has all of the specified permissions
   * @param permissionStrings - Array of permission strings
   * @returns True if user has all permissions
   */
  hasAllPermissions(permissionStrings: string[]): boolean {
    return permissionStrings.every(permission => this.hasPermission(permission));
  }

  isInternalUser(): boolean {
    return this.currentUser?.roles?.some(role => !role.isExternal) ?? false;
  }

  /**
   * Checks if the current user has the specified role.
   * Provider-agnostic — reads from the backend-hydrated currentUser, not from JWT claims.
   */
  isInRole(role: UserRole): boolean {
    return this.currentUser?.roles?.some(r => r.name === role) ?? false;
  }

  /**
   * Checks if the current user has any of the specified roles.
   */
  hasAnyRoles(roles: UserRole[]): boolean {
    return roles.some(role => this.isInRole(role));
  }

  /**
   * Returns the current user's ID, or null if not authenticated.
   */
  getUserId(): string | undefined {
    return this.currentUser?.id ?? undefined;
  }

  canManageLoans(): boolean {
    return this.isInRole(UserRole.Admin) || this.isInRole(UserRole.SecondaryAdmin);
  }

  canManageMyLoans(): boolean {
    return this.isInRole(UserRole.LoanOfficer) && !this.canManageLoans() && !this.isInRole(UserRole.BranchManager);
  }

  canManageBranchLoans(): boolean {
    return this.isInRole(UserRole.BranchManager) && !this.canManageLoans();
  }

  getRoles(): UserRole[] {
    return (this.currentUser?.roles?.map(r => r.name as UserRole).filter(Boolean) ?? []);
  }
}
